import './speakers.less'

import {Background} from '@codeurs/front'
import {Block, BlockType} from 'blocks/block'
import {Blocks} from 'blocks/blocks'
import {SpeakerModel} from 'models/program/SpeakerModel'
import React from 'react'
import {PageData} from 'types'
import useRouter from 'use-react-router'
import {useEvent} from 'util/eventcontext'
import {routeToParams, useSearchParams} from 'util/hooks'
import {style} from 'util/style'
import {Hero, HeroType} from 'views/hero/hero'
import {Layout} from 'views/layout'
import {Column, Grid} from 'views/layout/grid'
import {Summitlogo} from 'views/layout/summitlogo'
import {ProgramLink} from 'views/program/programlink'
import {SpeakersFilter} from 'views/speakers/speakersfilter'
import {Theme} from 'views/theme'

const styles = style('speakers', ['overview', 'item'])

export const Speakers: React.FC<PageData & {
	hero: HeroType
	blocks: Array<BlockType>
	more_speakers: boolean
}> = data => {
	const {title, hero, blocks, more_speakers} = data
	const event = useEvent()
	const router = useRouter()
	const searchParams = useSearchParams()
	const DEFAULT_FILTERS = {
		track: null,
		domain: null,
		sort: event.url === '/2024/world' ? 'default' : 'lastname',
		type: null
	}
	const filters = {...DEFAULT_FILTERS, ...searchParams}
	const setFilters = newFilters => {
		routeToParams(router, {...newFilters})
	}

	let speakers = event.program.speakers.filter(
		speaker => !speaker.data.hide_from_overview
	)
	if (filters.track) {
		speakers = speakers.filter(speaker => speaker.summitsSet.has(filters.track))
	}
	if (filters.type) {
		speakers = speakers.filter(speaker =>
			new Set(speaker.types).has(filters.type)
		)
	}
	if (filters.domain) {
		speakers = speakers.filter(
			speaker =>
				speaker.domains?.length && new Set(speaker.domains).has(filters.domain)
		)
	}
	switch (filters.sort) {
		case 'default':
			speakers.sort((a, b) =>
				a.summits.includes('itfworld') && b.summits.includes('itfworld')
					? a.firstname.localeCompare(b.firstname)
					: !a.summits.includes('itfworld') && b.summits.includes('itfworld')
					? 1
					: a.summits.includes('itfworld') && !b.summits.includes('itfworld')
					? -1
					: a.firstname.localeCompare(b.firstname)
			)
			break
		case 'firstname':
			speakers.sort((a, b) => a.firstname.localeCompare(b.firstname))
			break
		case 'lastname':
			speakers.sort((a, b) => a.lastname.localeCompare(b.lastname))
			break
	}

	return (
		<Layout page={data}>
			<div class={styles()}>
				<Hero pagetitle={title} {...hero} />
				<SpeakersFilter
					program={event.program}
					filters={filters}
					setFilters={setFilters}
				/>
				<SpeakersOverview speakers={speakers} more_speakers={more_speakers} />
				{blocks && blocks.length !== 0 && (
					<Blocks className={styles('blocks')}>
						{blocks.map((block, i) => (
							<Block key={i} {...block} />
						))}
					</Blocks>
				)}
			</div>
		</Layout>
	)
}

export const SpeakersOverview: React.FC<{
	speakers: Array<SpeakerModel>
	more_speakers: boolean
}> = ({speakers, more_speakers}) => {
	if (!speakers || speakers.length === 0) return null

	return (
		<div className={styles.overview()}>
			<Theme.OuterContainer>
				<Grid columns={1} s={2} m={2} l={3} xl={4} xxl={4} gap={[10, 10]}>
					{speakers.map(speaker => (
						<Column key={speaker.url}>
							<SpeakersItem speaker={speaker} />
						</Column>
					))}
					{more_speakers && (
						<Column>
							<SpeakersDummy label="More speakers to be revealed soon" />
						</Column>
					)}
				</Grid>
			</Theme.OuterContainer>
		</div>
	)
}

export const SpeakersItem: React.FC<{
	speaker: SpeakerModel
}> = ({speaker}) => {
	const {firstname, lastname, image, company} = speaker.data

	return (
		<div className={styles.item()}>
			<ProgramLink item={speaker}>
				<div className={styles.item('image')}>
					{image && (
						<Background img={image} className={styles.item('image-bg')} />
					)}
					<div className={styles.item('image-overlay')} />
				</div>
				<div className={styles.item('content')}>
					{(firstname || lastname) && (
						<p className={styles.item('content-name')}>
							{firstname + ' ' + lastname}
						</p>
					)}
					{company && (
						<p className={styles.item('content-company')}>{company}</p>
					)}
				</div>
				{speaker.summits.length && (
					<div className={styles.item('summit')}>
						{speaker.summits.map(summit => (
							<Summitlogo summit={summit} />
						))}
					</div>
				)}
			</ProgramLink>
		</div>
	)
}

export const SpeakersDummy: React.FC<{
	label: string
}> = ({label}) => {
	return (
		<div className={styles.item()}>
			<div className={styles.item('image')}>
				<div className={styles.item('image-overlay')} />
			</div>
			<div className={styles.item('content')}>
				<p className={styles.item('content-company')}>{label}</p>
			</div>
		</div>
	)
}
